import { cn } from '@/lib/utils';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: Variant;
    size?: Size;
    children: ReactNode;
}

const variantClasses: Record<Variant, string> = {
    primary: 'bg-[#159b96] text-white hover:bg-[#11837f] active:bg-[#0e726f] disabled:bg-[#a9d7d4]',
    secondary: 'bg-zinc-100 text-zinc-900 hover:bg-zinc-200 active:bg-zinc-300 disabled:bg-zinc-50 disabled:text-zinc-400',
    ghost: 'text-zinc-700 hover:bg-zinc-100 active:bg-zinc-200 disabled:text-zinc-300',
    danger: 'bg-red-500 text-white hover:bg-red-600 active:bg-red-700 disabled:bg-red-200',
    outline: 'border border-[#ccd7da] text-[#53636a] hover:bg-[#f1f8f8] active:bg-[#e7f3f2] disabled:border-zinc-200 disabled:text-zinc-300',
};

const sizeClasses: Record<Size, string> = {
    sm: 'h-7 px-2.5 text-xs gap-1',
    md: 'h-9 px-4 text-sm gap-1.5',
    lg: 'h-11 px-6 text-base gap-2',
};

export function Button({ variant = 'primary', size = 'md', className, children, ...props }: ButtonProps) {
    return (
        <button
            className={cn(
                'inline-flex items-center justify-center rounded-md font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#72c9c5] focus-visible:ring-offset-1',
                'disabled:cursor-not-allowed disabled:opacity-60',
                variantClasses[variant],
                sizeClasses[size],
                className,
            )}
            {...props}
        >
            {children}
        </button>
    );
}
